import { Button, Input } from '@bs/ui-components'
import { CheckCircle2, LoaderCircle, ShieldCheck } from 'lucide-react'
import { FormEvent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useAuth } from '@/app/use-auth'
import { getClaimInfo } from '@/features/auth/api'
import { AuthPageShell, FormError } from './auth-page-shell'

interface ClaimInfo {
  email: string
  firstName?: string
  lastName?: string
  hasAccount: boolean
}

export function ClaimProfilePage() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const { claimProfile } = useAuth()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const [claimInfo, setClaimInfo] = useState<ClaimInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    let cancelled = false
    getClaimInfo(token)
      .then((info) => {
        if (cancelled) return
        setClaimInfo(info)
        setEmail(info.email)
        setName([info.firstName, info.lastName].filter(Boolean).join(' '))
      })
      .catch(() => {
        if (!cancelled) setLoadError('This claim link is invalid or has already been used.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  async function claim(event: FormEvent) {
    event.preventDefault()

    if (!claimInfo) return
    const needsPassword = !claimInfo.hasAccount
    if (needsPassword && password.length < 8) return setError('Password must contain at least 8 characters.')
    setIsSubmitting(true)
    setError('')
    try {
      const [first, ...rest] = name.trim().split(/\s+/)
      await claimProfile(token, {
        ...(needsPassword ? { password } : {}),
        ...(needsPassword ? { name: { ...(first ? { first } : {}), last: rest.join(' ') } } : {}),
      })
      navigate('/my-profile', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to claim this profile. Try again.')
      setIsSubmitting(false)
    }
  }

  if (loading) {
    return (
      <AuthPageShell>
        <div className="flex w-full max-w-[500px] flex-col items-center text-[#404040]">
          <LoaderCircle className="size-10 animate-spin text-accent" />
          <p className="mt-4 text-muted-foreground">Checking your invitation…</p>
        </div>
      </AuthPageShell>
    )
  }

  if (loadError || !claimInfo) {
    return (
      <AuthPageShell>
        <div className="w-full max-w-[500px] text-center text-[#404040]">
          <ShieldCheck className="mx-auto size-14 text-muted-foreground" />
          <h1 className="mt-5 text-[26px] font-medium">Invitation not found</h1>
          <p className="mt-3 leading-6 text-muted-foreground">{loadError || 'This claim link is invalid or has already been used.'}</p>
        </div>
      </AuthPageShell>
    )
  }

  const needsPassword = !claimInfo?.hasAccount

  return (
    <AuthPageShell>
      <form onSubmit={claim} className="w-full max-w-[500px] text-[#404040]">
        <ShieldCheck className="mx-auto size-14 text-accent" />
        <h1 className="mt-5 text-center text-[30px] font-medium">Claim your profile</h1>
        <p className="mt-3 text-center leading-6 text-muted-foreground">
          {needsPassword
            ? 'Review your details and create a password to finish claiming your talent profile.'
            : 'Welcome back — this profile is already yours. Confirm below to finish claiming it.'}
        </p>
        {error ? <FormError message={error} /> : null}
        <div className="mt-8 grid gap-5">
          <label className="grid gap-2 text-sm font-medium">
            Full name
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Enter full name" required disabled={!needsPassword} />
          </label>
          <label className="grid gap-2 text-sm font-medium">
            Email
            <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="Enter email" required disabled />
          </label>
          {needsPassword ? (
            <label className="grid gap-2 text-sm font-medium">
              Create password
              <Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="At least 8 characters" minLength={8} required />
            </label>
          ) : null}
        </div>
        <Button type="submit" variant="accent" size="lg" className="mt-8 h-[50px] w-full text-lg" disabled={isSubmitting}>
          {isSubmitting ? <LoaderCircle className="size-5 animate-spin" /> : <CheckCircle2 className="size-5" />}
          {isSubmitting ? 'Claiming…' : 'Claim profile'}
        </Button>
      </form>
    </AuthPageShell>
  )
}
